import { getOrganizationsByLength } from './_api';
import { getLevenshteinDecimal } from '../../../../utils/strings/StringUtil';
import { compareFn } from '../../../../utils/objects/ObjectUtil';
import { MINIMUM_LENGTH, MINIMUM_SIMILARITY } from './_constants';

/**
 * Identifies third-party organizations similar to @param value.
 * @description
 * Retrieves third-party organizations whose similarity to value of interest exceeds pre-defined, minimum fraction.
 * Similarity represented by Levenshtein Distance between sequences converted to fractional likeness of one sequence to other.
 * To improve efficiency, the similarity calculation is only performed on third-party organizations that could exceed the pre-defined, minimum fraction.
 * This is accomplished by first retrieving the subset of third-party organizations whose length would not immediately disqualify it from meeting the
 * minimum fraction likeness.
 *
 * A two-character sequence will always be at most .25 similar to an eight-character sequence and can be ignored
 * if that maximum possible similarity will not meet the minimum desired similarity.
 *
 * @param {string} value value of interest to perform similarity checks against.
 * @returns {Promise<string[]>} array of similar third-party organizations.
 */
export const checkSimilarity = async value => {
	// calculate absolute value of acceptable difference in length
	const threshold = Math.round(value.length * (1 - MINIMUM_SIMILARITY));

	const min =
		value.length - threshold > MINIMUM_LENGTH
			? value.length - threshold
			: MINIMUM_LENGTH;
	const max = value.length + threshold;

	// retrieve subset of organizations that _could_ meet threshold
	const arrOrganizations = await getOrganizationsByLength(min, max);

	// retrieve from subset of organizations those that do meet threshold
	let arrLikeOrganizations = [];
	arrOrganizations.forEach(org => {
		const similarity = getLevenshteinDecimal(
			value.replace(/[^a-zA-Z0-9]/g, ''),
			org.replace(/[^a-zA-Z0-9]/g, '')
		);
		if (similarity >= MINIMUM_SIMILARITY) {
			arrLikeOrganizations.push({
				name: org,
				similarity: similarity,
			});
		}
	});
	// sort by descending similarity
	arrLikeOrganizations.sort((firstEl, secondEl) =>
		compareFn(firstEl, secondEl, 'similarity', -1)
	);

	return arrLikeOrganizations.map(org => org.name);
};
